<template>
	<div class="signature-step">

		<!-- En-tête -->
		<div class="text-center mb-4">
			<div class="sig-hourglass bg-primary mb-2">
				<font-awesome-icon v-if="phase !== 'signed'" :icon="['far', 'hourglass']" />
				<font-awesome-icon v-else :icon="['fal', 'file-check']" />
			</div>
			<h5 class="font-weight-bold mb-1">
				{{ phase === 'signed' ? $t('monte.contrat_signe') : $t('monte.payment_step_title_1') }}
			</h5>
			<p v-if="phase !== 'signed'" class="text-muted small mb-0">
				{{ $t('monte.payment_step_title_2') }}
			</p>
		</div>

		<!-- Étape 1 : Initialisation (toujours faite) -->
		<div class="sig-step-row mb-4">
			<div class="sig-step-badge bg-primary text-white">
				<font-awesome-icon :icon="['fal', 'file-invoice']" />
				<font-awesome-icon :icon="['fal', 'check-circle']" class="sig-step-check text-primary" />
			</div>
			<span class="ml-3 font-weight-bold">{{ $t('monte.step_init_contrat') }}</span>
		</div>

		<!-- Étape 2 : Paiement -->
		<div class="sig-step-row mb-4">
			<div
				class="sig-step-badge text-white"
				:class="paymentStepDone ? 'bg-primary' : 'sig-step-badge-pending text-primary'"
			>
				<font-awesome-icon :icon="['fal', 'credit-card']" />
				<font-awesome-icon v-if="paymentStepDone" :icon="['fal', 'check-circle']" class="sig-step-check text-primary" />
			</div>
			<div class="ml-3 flex-grow-1 w-100">
				<p class="font-weight-bold mb-1">{{ $t('monte.step_paiement') }}</p>

				<!-- Phase payment : bouton Régler -->
				<template v-if="phase === 'payment'">
					<button
						type="button"
						class="btn btn-primary btn-block mt-2"
						:disabled="paying"
						@click.prevent="$emit('pay')"
					>
						<b-spinner v-if="paying" small class="mr-1" />
						<font-awesome-icon v-else :icon="['fal', 'credit-card']" class="mr-1" />
						<span v-if="amount > 0">{{ $t('monte.regler_montant', { amount: formattedAmount }) }}</span>
						<span v-else>{{ $t('monte.regler_frais') }}</span>
					</button>
				</template>

				<!-- Phase awaiting_payment : polling en cours -->
				<template v-else-if="phase === 'awaiting_payment'">
					<div class="d-flex align-items-center mt-2">
						<b-spinner small variant="primary" class="mr-2" />
						<span class="text-muted small">{{ $t('monte.en_attente_retour_banque') }}</span>
					</div>
				</template>

				<!-- Phase signing/signed : facture dispo -->
				<template v-else-if="invoice">
					<button
						type="button"
						class="btn btn-link p-0"
						:disabled="downloadingInvoice"
						@click.prevent="downloadInvoice"
					>
						<b-spinner v-if="downloadingInvoice" small class="mr-1" />
						<font-awesome-icon v-else :icon="['fal', 'download']" class="mr-1" />
						{{ $t('monte.telecharger_ma_facture') }}
					</button>
				</template>
			</div>
		</div>

		<!-- Étape 3 : Signature Yousign -->
		<div class="sig-step-row mb-4">
			<div
				class="sig-step-badge text-white"
				:class="phase === 'signed' ? 'bg-primary' : 'sig-step-badge-pending text-primary'"
			>
				<font-awesome-icon :icon="['fal', 'signature']" />
				<font-awesome-icon v-if="phase === 'signed'" :icon="['fal', 'check-circle']" class="sig-step-check text-primary" />
			</div>
			<div class="ml-3 flex-grow-1 w-100">
				<p class="font-weight-bold mb-1">{{ $t('monte.step_signature_contrat') }}</p>

				<!-- Phase signing : bouton signer + auto-polling ou vérif manuelle -->
				<template v-if="phase === 'signing'">
					<p class="text-muted small mb-2">{{ $t('monte.en_attente_signature_yousign') }}</p>
					<button
						v-if="signatureUrl"
						type="button"
						class="btn btn-primary btn-block mt-1"
						@click.prevent="openYousign"
					>
						<font-awesome-icon :icon="['fal', 'pen-fancy']" class="mr-1" />
						{{ $t('monte.signer_via_yousign') }}
					</button>

					<!-- Polling automatique en cours -->
					<div v-if="signaturePollingActive" class="d-flex align-items-center mt-2">
						<b-spinner small variant="primary" class="mr-2" />
						<span class="text-muted small">{{ $t('monte.verification_signature_auto') }}</span>
					</div>

					<!-- Polling terminé sans succès : boutons manuels -->
					<template v-if="signaturePollingExpired">
						<p class="text-muted small mt-2 mb-1">{{ $t('monte.signature_polling_expired') }}</p>
						<button
							type="button"
							class="btn btn-outline-primary btn-block mt-1"
							:disabled="checkingSigned"
							@click.prevent="manualCheckSignature"
						>
							<b-spinner v-if="checkingSigned" small class="mr-1" />
							<font-awesome-icon v-else :icon="['fal', 'sync']" class="mr-1" />
							{{ $t('monte.verifier_si_signe') }}
						</button>
						<button
							v-if="signatureUrl"
							type="button"
							class="btn btn-outline-secondary btn-block mt-1"
							@click.prevent="openYousign"
						>
							<font-awesome-icon :icon="['fal', 'pen-fancy']" class="mr-1" />
							{{ $t('monte.rouvrir_yousign') }}
						</button>
					</template>
				</template>

				<!-- Phase signed : télécharger contrat signé -->
				<template v-else-if="phase === 'signed'">
					<button
						type="button"
						class="btn btn-link p-0"
						:disabled="downloadingContract"
						@click.prevent="$emit('download-contract')"
					>
						<b-spinner v-if="downloadingContract" small class="mr-1" />
						<font-awesome-icon v-else :icon="['fal', 'download']" class="mr-1" />
						{{ $t('monte.telecharger_contrat_signe') }}
					</button>
				</template>
			</div>
		</div>

		<!-- Besoin d'aide -->
		<div class="text-center mt-4">
			<p class="font-weight-bold mb-2">{{ $t('monte.besoin_aide') }}</p>
			<router-link :to="{ name: 'contactUs' }" class="btn btn-outline-primary">
				<font-awesome-icon :icon="['fal', 'envelope']" class="mr-1" />
				{{ $t('monte.contactez_nous') }}
			</router-link>
		</div>

	</div>
</template>

<script>
import InvoiceMixin from '@/mixins/Invoice.js'
import ContractMixin from '@/mixins/Contract.js'

export default {
	name: 'PaymentStep',
	mixins: [InvoiceMixin, ContractMixin],
	props: {
		/** Montant total à payer en euros. */
		amount: {
			type: Number,
			default: 0,
		},
		/** Traitement en cours (désactive le bouton Régler). */
		paying: {
			type: Boolean,
			default: false,
		},
		/**
		 * Phase courante du flow :
		 * - 'payment'          : en attente du règlement
		 * - 'awaiting_payment' : paiement en cours de vérification (polling)
		 * - 'signing'          : règlement effectué, en attente de signature Yousign
		 * - 'signed'           : contrat signé
		 */
		phase: {
			type: String,
			default: 'payment',
			validator: v => ['payment', 'awaiting_payment', 'signing', 'signed'].includes(v),
		},
		/** URL Yousign pour signer le contrat (disponible après finalisation). */
		signatureUrl: {
			type: String,
			default: null,
		},
		/** Première facture du contrat (disponible après finalisation). */
		invoice: {
			type: Object,
			default: null,
		},
		/** Vérification de signature en cours. */
		checkingSigned: {
			type: Boolean,
			default: false,
		},
		/** Téléchargement du contrat signé en cours. */
		downloadingContract: {
			type: Boolean,
			default: false,
		},
		/** Identifiant du contrat (nécessaire pour le polling paiement/signature). */
		contractId: {
			type: Number,
			default: null,
		},
	},
	emits: ['pay', 'check-signed', 'download-contract', 'payment-confirmed', 'signature-confirmed'],
	data() {
		return {
			downloadingInvoice: false,
			// Polling paiement
			paymentPollTimer: null,
			// Polling signature
			signaturePollTimer: null,
			signaturePollingActive: false,
			signaturePollingExpired: false,
			signaturePollCount: 0,
		}
	},
	computed: {
		formattedAmount() {
			return this.priceFormat(this.amount, 'EUR', true)
		},
		paymentStepDone() {
			return ['signing', 'signed'].includes(this.phase)
		},
	},
	watch: {
		phase(newPhase, oldPhase) {
			// Démarrer le polling paiement quand on entre en awaiting_payment
			if (newPhase === 'awaiting_payment') {
				this.startPaymentPolling()
			} else {
				this.stopPaymentPolling()
			}

			// Démarrer le polling signature quand on entre en signing
			if (newPhase === 'signing' && oldPhase !== 'signing') {
				this.startSignaturePolling()
			}

			// Arrêter tout polling quand signé
			if (newPhase === 'signed') {
				this.stopPaymentPolling()
				this.stopSignaturePolling()
			}
		},
	},
	beforeDestroy() {
		this.stopPaymentPolling()
		this.stopSignaturePolling()
	},
	mounted() {
		if (this.phase === 'awaiting_payment') {
			this.startPaymentPolling()
		} else if (this.phase === 'signing') {
			this.startSignaturePolling()
		}
	},
	methods: {
		openYousign() {
			if (this.signatureUrl) {
				window.open(this.signatureUrl, '_blank')
			}
		},
		async downloadInvoice() {
			if (!this.invoice) return
			this.downloadingInvoice = true
			try {
				await this.getAddressedPdfInvoice([this.invoice])
			} catch (e) {
				console.error('PaymentStep::downloadInvoice => ERROR', e)
				this.failureToast('monte.print_error')
			} finally {
				this.downloadingInvoice = false
			}
		},

		// ─── Polling paiement (toutes les 3s) ───
		startPaymentPolling() {
			this.stopPaymentPolling()
			this.paymentPollTimer = setInterval(() => {
				this.pollPaymentStatus()
			}, 3000)
		},
		stopPaymentPolling() {
			if (this.paymentPollTimer) {
				clearInterval(this.paymentPollTimer)
				this.paymentPollTimer = null
			}
		},
		async pollPaymentStatus() {
			if (!this.contractId) return
			try {
				const result = await this.checkPaymentResult([this.contractId])
				if (!result) return
				if (result.status === 'success') {
					this.stopPaymentPolling()
					this.$emit('payment-confirmed')
				} else if (result.status === 'error') {
					this.stopPaymentPolling()
					this.failureToast('monte.erreur_paiement')
				}
				// status === 'pending' → continue polling
			} catch (e) {
				console.error('PaymentStep::pollPaymentStatus => ERROR', e)
			}
		},

		// ─── Polling signature (toutes les 5s pendant 60s max) ───
		startSignaturePolling() {
			this.stopSignaturePolling()
			this.signaturePollingActive = true
			this.signaturePollingExpired = false
			this.signaturePollCount = 0

			this.signaturePollTimer = setInterval(() => {
				this.signaturePollCount++
				// 60s / 5s = 12 cycles max
				if (this.signaturePollCount >= 12) {
					this.stopSignaturePolling()
					this.signaturePollingExpired = true
					return
				}
				this.pollSignatureStatus()
			}, 5000)
		},
		stopSignaturePolling() {
			this.signaturePollingActive = false
			if (this.signaturePollTimer) {
				clearInterval(this.signaturePollTimer)
				this.signaturePollTimer = null
			}
		},
		async pollSignatureStatus() {
			if (!this.contractId) return
			try {
				const status = await this.getContractSignatureStatus(this.contractId)
				if (status && status.signed) {
					this.stopSignaturePolling()
					this.$emit('signature-confirmed')
				}
			} catch (e) {
				console.error('PaymentStep::pollSignatureStatus => ERROR', e)
			}
		},

		/** Vérification manuelle de la signature (après expiration du polling). */
		async manualCheckSignature() {
			this.$emit('check-signed')
		},
	},
}
</script>
